import React, { useCallback, useId, useRef, useState } from 'react';

import Drawer from 'common/components/Drawer';
import { isIOSDevice } from 'common/DeviceDetector';
import { EventBusEvent } from 'common/enums/EventBus';
import type { DrawerEventProps } from 'common/hooks/drawer';
import { useKeyboardClose } from 'common/hooks/keyboard';
import { useSubscriptionCallback } from 'common/hooks/subscription';
import { getCurrentUrl } from 'common/utils/url';
import Container from './Container';
import Content from './Content';
import Header from './Header';

const pushHistoryState = () => history.pushState(null, '', getCurrentUrl());

// eslint-disable-next-line max-lines-per-function
const CommonDrawer = () => {
  const ariaLabelId = useId();
  const drawersRef = useRef<DrawerEventProps[]>([]);
  const [drawer, setDrawer] = useState<DrawerEventProps>({ open: false });

  const handleNestedDrawerClose = ({ onCloseComplete }) => {
    const previousDrawer = drawersRef.current[drawersRef.current.length - 1];
    setDrawer(previousDrawer);
    onCloseComplete?.();
  };

  const handleFirstDrawerClose = ({ transitionDisabled = false, onCloseComplete }) => {
    window.removeEventListener('popstate', handleNavigationDrawerClose);
    setDrawer({ transitionDisabled, open: false });
    onCloseComplete?.();
  };

  const handleDrawerClose = () => {
    if (drawer?.withCloseDisabled) {
      return;
    }

    const { onCloseComplete } = drawersRef.current.pop();

    if (drawersRef.current.length > 0) {
      handleNestedDrawerClose({ onCloseComplete });
    } else {
      handleFirstDrawerClose({
        onCloseComplete: () => {
          history.back();
          onCloseComplete?.();
        },
      });
    }
  };

  const handleNavigationDrawerClose = useCallback(() => {
    const { onCloseComplete } = drawersRef.current.pop();

    if (drawersRef.current.length > 0) {
      handleNestedDrawerClose({ onCloseComplete });
    } else {
      handleFirstDrawerClose({ onCloseComplete, transitionDisabled: isIOSDevice() });
    }
  }, []);

  const handleEventData = useCallback(
    (newDrawer: DrawerEventProps) => {
      const handleDrawerOpen = () => {
        drawersRef.current.push(newDrawer);
        pushHistoryState();
        setDrawer(newDrawer);
      };

      const handleFirstDrawerOpen = () => {
        handleDrawerOpen();
        window.addEventListener('popstate', handleNavigationDrawerClose);
      };

      const isUpdateEvent = newDrawer.eventType === 'update';

      const isFirstDrawerBeingOpened = drawer.open === false && newDrawer.open === true;
      const isDrawerBeingClosed = drawer.open === true && newDrawer.open === false;
      const isNestedDrawerBeingOpened = drawer.open === true && newDrawer.open === true;

      if (isUpdateEvent) {
        const isSameDrawer = drawer.drawerKey === newDrawer.drawerKey;

        if (isSameDrawer) {
          setDrawer(newDrawer);
        }
      } else if (isFirstDrawerBeingOpened) {
        handleFirstDrawerOpen();
      } else if (isDrawerBeingClosed) {
        handleDrawerClose();
      } else if (isNestedDrawerBeingOpened) {
        handleDrawerOpen();
      }
    },
    [drawer.open, handleNavigationDrawerClose]
  );

  useSubscriptionCallback<DrawerEventProps>(EventBusEvent.ToggleDrawer, handleEventData);

  useKeyboardClose(drawer.open && !drawer.withCloseDisabled, handleDrawerClose);

  const {
    open,
    title,
    content,
    p,
    html,
    backgroundColor,
    width,
    withBackButton,
    backButtonAriaLabel,
    incrementZIndex,
    transitionDisabled,
    withCloseDisabled = false,
    withBodyFocus = true,
  } = drawer;

  return (
    <Drawer
      isOpen={open}
      width={width}
      ariaLabelledby={ariaLabelId}
      onBackdropClick={withCloseDisabled ? null : handleDrawerClose}
      transitionDisabled={transitionDisabled}
      {...(incrementZIndex ? { incrementZIndex } : {})}
    >
      <Container data-corgi-id="common-disclaimer">
        {open && (
          <Header
            id={ariaLabelId}
            title={title}
            onClick={handleDrawerClose}
            withBackButton={withBackButton}
            backButtonAriaLabel={backButtonAriaLabel}
            withCloseDisabled={withCloseDisabled}
          />
        )}
        <Content
          p={p}
          html={html}
          content={content}
          backgroundColor={backgroundColor}
          withBodyFocus={withBodyFocus}
        />
      </Container>
    </Drawer>
  );
};

export default CommonDrawer;
